import { useCallback, useEffect, useState } from 'react';
import cross from '~/assets/icons/cross.svg';
import user from '~/assets/icons/user.svg';
import { playSound, Sound } from '~/assets/sounds';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { Text } from '~/components/ui/Text';
import { IPlayerSearchResult, rage, useInputAction } from '~/rage';
import { InputAction } from '~/shared';
import {
	setPlayerIndex,
	setPlayerIndexNext,
	setPlayerIndexPrev,
	setPlayerMaxIndex,
	setSelectedPlayer,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { Divider } from '../Divider';
import { PlayerInput } from '../PlayerInput';
import {
	StyledContainer,
	StyledCrossButton,
	StyledHeader,
	StyledHoverBackground,
	StyledPlayerItemContainer,
	StyledPlayerList,
} from './PlayerSelect.styled';

const PlayerSelect: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const [players, setPlayers] = useState<IPlayerSearchResult[]>([]);
	const [playerSearch, setPlayerSearch] = useState<string>('');
	const [selectionHidden, setSelectionHidden] = useState<boolean>(false);
	const playerIndex = useLayoutSelector(
		(state) => state.propertyExchange.playerIndex,
	);

	const handleSelect = useCallback(async (player: IPlayerSearchResult) => {
		if (!player) {
			return;
		}
		await rage.exchange.invite(player.characterId);
		dispatch(setSelectedPlayer(player));
	}, []);

	useInputAction(InputAction.CommonMenuNavUp, () =>
		dispatch(setPlayerIndexPrev()),
	);
	useInputAction(InputAction.CommonMenuNavDown, () =>
		dispatch(setPlayerIndexNext()),
	);
	useInputAction(InputAction.CommonMenuAccept, async () => {
		await rage.exchange.invite(players[playerIndex].characterId);
		dispatch(setSelectedPlayer(players[playerIndex]));
	});

	useEffect(() => {
		rage.exchange
			.findPlayers(playerSearch)
			.then((filteredPlayers) => {
				setPlayers(filteredPlayers);
			})
			.catch((err) => rage.notifications.showException(err));
	}, [playerSearch]);

	useEffect(() => {
		dispatch(setPlayerMaxIndex(players.length - 1));
	}, [players]);

	useEffect(() => {
		const playerDiv = document.getElementById(`player-${playerIndex}`);
		if (playerDiv !== null) {
			setTimeout(() => {
				playerDiv.scrollIntoView({ block: 'nearest' });
			}, 50);
		}
	}, [playerIndex]);

	return (
		<StyledContainer>
			<StyledHeader>
				<Text>Выберите игрока для обмена</Text>
				<StyledCrossButton
					onClick={async () => {
						await rage.exchange.exitExchange();
					}}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				>
					<img src={cross} />
				</StyledCrossButton>
			</StyledHeader>
			<Divider />
			<PlayerInput
				value={playerSearch}
				items={players}
				onChange={setPlayerSearch}
			/>
			<StyledPlayerList>
				{players.map((item, index) => (
					<StyledPlayerItemContainer
						key={index}
						$active={index === playerIndex}
						id={`player-${index}`}
						onClick={() => dispatch(setPlayerIndex(index))}
						onMouseEnter={() => {
							setSelectionHidden(true);
							playSound(Sound.LinkCursor);
						}}
						onMouseLeave={() => setSelectionHidden(false)}
						$selectionHidden={selectionHidden && index === playerIndex}
					>
						<StyledHoverBackground />
						<img src={user} />
						<Text>{item.nickname}</Text>
						<Button
							color={ButtonColors.Select2}
							height={ButtonHeight.Height30}
							onClick={() => handleSelect(item)}
							style={{ zIndex: 2 }}
						>
							<Text>Выбрать</Text>
						</Button>
					</StyledPlayerItemContainer>
				))}
			</StyledPlayerList>
		</StyledContainer>
	);
};

export { PlayerSelect };
